import { useApolloClient } from "@apollo/client/react";
import { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { Link, useLocation } from "react-router";
import { logout, redirectToLogin } from "@/auth/actions";
import { Button } from "@/components/design/Button";
import { ClaimListingApplicationDocument } from "@/graphql/generated/operations";
import { publicClient } from "@/graphql/public-client";
import { ConfirmPublicListingApplicationDocument } from "@/graphql/generated/public";
import { PUBLIC_SHARE_HOME } from "@/components/layout/PublicShareShell";
import { getConfig } from "@/lib/config";
import { useAuthStore } from "@/stores/auth-store";
import { forgetClaimedApplication } from "./application-cache";
import { heldToken, holdToken, releaseToken, tokenFromFragment } from "./confirmation-token";

/** "public": the confirmation of the mailbox, for everybody; "account": its continuation as the signed-in account. */
type Step = "public" | "account";
/** How the confirmation ended; "processed" is the answer without an outcome, worded neutrally as before. */
type Ending = "CONFIRMED" | "ALREADY_APPLIED" | "NOT_CONFIRMABLE" | "processed";
/** A step that did not end: the same click may be repeated. */
type Notice = "RETRY_LATER" | "WRONG_ACCOUNT" | "failed";

function start(): { token: string | null; step: Step } {
  const fresh = tokenFromFragment(window.location.hash);
  if (fresh) {
    // A new link replaces whatever an earlier one left waiting for a login.
    releaseToken();
    return { token: fresh, step: "public" };
  }
  const held = heldToken();
  return { token: held, step: held ? "account" : "public" };
}

// Die Seite hinter dem Link der Bestätigungsmail (@external-adr GENESIS-0072-applying-without-an-account-confirms-email-before-a-person-exists,
// @adr SPARK-0058-public-applications-use-two-narrow-bff-endpoints@1). Der Token steht im Fragment, damit kein Server ihn
// in einem Log sieht; die Seite nimmt ihn sofort aus der sichtbaren URL. Erst ein Klick bestätigt: ein Linkscanner, der
// die Seite nur öffnet, bestätigt nichts. Gehört zum Postfach ein Konto, hält die Seite den Token nur für den Weg über
// die Anmeldung im Speicher dieses Tabs und setzt danach, wieder erst auf Klick, genau diese Bewerbung als die
// angemeldete Person fort. Sonst geht der Token in keinen Speicher des Browsers.
export function ConfirmListingApplicationPage() {
  const location = useLocation();
  const client = useApolloClient();
  const signedIn = useAuthStore((state) => state.status === "authenticated");
  const [initial] = useState(start);
  const [token, setToken] = useState(initial.token);
  const [step, setStep] = useState<Step>(initial.step);
  const [pending, setPending] = useState(false);
  const [ending, setEnding] = useState<Ending | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  // Back to this page after the login, without the token: it waits in the tab, never in a URL.
  const here = `${getConfig().basePath.replace(/\/$/, "")}${location.pathname}`;

  useEffect(() => {
    if (window.location.hash) {
      window.history.replaceState(window.history.state, "", `${window.location.pathname}${window.location.search}`);
    }
  }, []);

  function end(how: Ending, from: Step) {
    releaseToken();
    setToken(null);
    setEnding(how);
    if (from === "account" && how !== "NOT_CONFIRMABLE") forgetClaimedApplication(client.cache);
  }

  function settle(outcome: string | null, from: Step, value: string) {
    switch (outcome) {
      case "CONFIRMED":
      case "ALREADY_APPLIED":
      case "NOT_CONFIRMABLE":
        end(outcome, from);
        return;
      case "LOGIN_REQUIRED":
        // Nothing was filed and the token stays valid: it waits for the login of the account it belongs to.
        holdToken(value);
        setStep("account");
        return;
      case "RETRY_LATER":
        setNotice("RETRY_LATER");
        return;
      case "WRONG_ACCOUNT":
        setNotice(from === "account" ? "WRONG_ACCOUNT" : "failed");
        return;
      default:
        end("processed", from);
    }
  }

  async function submit() {
    const value = token;
    if (!value || pending || ending) return;
    const from = step;
    setPending(true);
    setNotice(null);
    try {
      if (from === "public") {
        const { data } = await publicClient.mutate({
          mutation: ConfirmPublicListingApplicationDocument,
          variables: { token: value },
        });
        if (!data) throw new Error("No answer");
        settle(data.confirmListingApplication.outcome ?? null, from, value);
      } else {
        const { data } = await client.mutate({
          mutation: ClaimListingApplicationDocument,
          variables: { input: { token: value } },
        });
        if (!data) throw new Error("No answer");
        settle(data.claimListingApplication.outcome, from, value);
      }
    } catch {
      // No readable answer: genesis may or may not have taken it, so the page claims neither. The token stays; repeating
      // the click is harmless, a spent token answers like any other.
      setNotice("failed");
    } finally {
      setPending(false);
    }
  }

  function signIn() {
    if (token) holdToken(token);
    redirectToLogin(here);
  }

  const busyLabel = <FormattedMessage id="share.public.confirm.sending" defaultMessage="Wird bestätigt…" />;

  return (
    <main id="confirm-application" className="w-full max-w-[720px] px-4 pb-12 pt-10 @sm:px-6 @lg:px-10" data-graphql="ConfirmPublicListingApplication">
      <h1 className="smycles-page-title">
        <FormattedMessage id="share.public.confirm.title" defaultMessage="Bewerbung bestätigen" />
      </h1>
      <div className="box mt-6 bg-surface p-5">
        {ending ? (
          <p id="confirm-application-done" role="status" className="text-smycles-body text-ink">
            {ending === "CONFIRMED" ? (
              <FormattedMessage id="share.public.confirm.confirmed" defaultMessage="Danke, deine Bewerbung ist eingegangen." />
            ) : ending === "ALREADY_APPLIED" ? (
              <FormattedMessage
                id="share.public.confirm.alreadyApplied"
                defaultMessage="Du hast dich auf diese Ausschreibung bereits beworben."
              />
            ) : ending === "NOT_CONFIRMABLE" ? (
              <FormattedMessage
                id="share.public.confirm.notConfirmable"
                defaultMessage="Dieser Link ist abgelaufen oder wurde bereits verwendet."
              />
            ) : (
              <FormattedMessage
                id="share.public.confirm.done"
                defaultMessage="Wir haben deine Bestätigung verarbeitet. Wenn die Bewerbung erfolgreich eingegangen ist, erhältst du eine Eingangsbestätigung."
              />
            )}
          </p>
        ) : !token ? (
          <p id="confirm-application-missing" className="text-smycles-body text-ink">
            <FormattedMessage
              id="share.public.confirm.missing"
              defaultMessage="Dieser Seite fehlt der Bestätigungslink. Öffne bitte den Link aus deiner E-Mail noch einmal."
            />
          </p>
        ) : step === "public" ? (
          <>
            <p className="text-smycles-body text-ink">
              <FormattedMessage
                id="share.public.confirm.explain"
                defaultMessage="Mit diesem Schritt bestätigst du deine E-Mail-Adresse und schickst die Bewerbung ab."
              />
            </p>
            <Button id="confirm-application-submit" className="mt-5" disabled={pending} onClick={() => void submit()}>
              {pending ? busyLabel : (
                <FormattedMessage id="share.public.confirm.submit" defaultMessage="Bewerbung bestätigen" />
              )}
            </Button>
          </>
        ) : signedIn ? (
          <>
            <p id="confirm-application-account" className="text-smycles-body text-ink">
              <FormattedMessage
                id="share.public.confirm.account"
                defaultMessage="Zu dieser E-Mail-Adresse gibt es ein Konto. Schließe die Bewerbung mit deinem Konto ab; deine Angaben und Unterlagen aus dem Formular werden übernommen."
              />
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Button id="confirm-application-claim" disabled={pending} onClick={() => void submit()}>
                {pending ? busyLabel : (
                  <FormattedMessage id="share.public.confirm.claim" defaultMessage="Bewerbung abschließen" />
                )}
              </Button>
              {notice === "WRONG_ACCOUNT" ? (
                <Button id="confirm-application-switch-account" variant="secondary" disabled={pending}
                  onClick={() => void logout(here)}>
                  <FormattedMessage id="share.public.confirm.switchAccount" defaultMessage="Mit anderem Konto anmelden" />
                </Button>
              ) : null}
            </div>
          </>
        ) : (
          <>
            <p id="confirm-application-login-required" className="text-smycles-body text-ink">
              <FormattedMessage
                id="share.public.confirm.loginRequired"
                defaultMessage="Zu dieser E-Mail-Adresse gibt es bereits ein Konto. Bitte melde dich an, um deine Bewerbung abzuschließen."
              />
            </p>
            <Button id="confirm-application-login" className="mt-5" onClick={signIn}>
              <FormattedMessage id="share.public.confirm.login" defaultMessage="Anmelden und fortfahren" />
            </Button>
          </>
        )}
        {notice && !ending ? (
          <p id="confirm-application-error" role="alert" className="mt-3 text-smycles-meta text-ink-accent">
            {notice === "RETRY_LATER" ? (
              <FormattedMessage
                id="share.public.confirm.retryLater"
                defaultMessage="Das hat gerade nicht geklappt. Bitte versuche es in einem Moment erneut."
              />
            ) : notice === "WRONG_ACCOUNT" ? (
              <FormattedMessage
                id="share.public.confirm.wrongAccount"
                defaultMessage="Du bist mit einem anderen Konto angemeldet. Bitte melde dich mit dem Konto zu dieser E-Mail-Adresse an."
              />
            ) : (
              <FormattedMessage
                id="share.public.confirm.failed"
                defaultMessage="Wir konnten das Ergebnis der Bestätigung nicht prüfen. Du kannst es erneut versuchen."
              />
            )}
          </p>
        ) : null}
      </div>
      <Link to={PUBLIC_SHARE_HOME} className="mt-6 inline-flex text-smycles-micro uppercase tracking-[0.2em] text-ink-muted transition hover:text-ink-accent">
        <FormattedMessage id="share.openListings.title" defaultMessage="Offene Ausschreibungen" />
      </Link>
    </main>
  );
}
